import React from 'react';
import { Button, Card, CardContent, Grid } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { Fitted, PropertyType, SizeUnit, useRegionsQuery } from 'src/generated/graphql';
import { sizeUnits } from 'src/libs/utils/size';
import { formatNumber, orderMediaPosition } from 'src/libs/utils';
import { TubeStation } from 'src/components/TubeStation';
import { AvailablePropertyFragmentExtra } from '../../AvailableProperties/AvailableProperties';
import {
    CardSlider
} from '../../AvailableProperties/components/Properties/components/PropertyCard/components/CardSlider';
import { Name } from '../../AvailableProperties/components/Properties/components/PropertyCard/components/Name';
import {
    PropertyInfoItem
} from '../../AvailableProperties/components/Properties/components/PropertyCard/components/PropertyInfoItem';

const useStyles = makeStyles((theme) => ({
    card: {
        height: '427px',
        border: `1px solid ${theme.palette.primary.main}`,
        cursor: 'pointer',
    },
    cardContent: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    cardContentContainer: {
        flex: '1 0 auto',
    },
    cardActionsContainer: {
        display: 'flex',
        alignItems: 'end',
        justifyContent: 'center',
        flex: '1 0 auto',
    },
    cardActionButton: {
        margin: '0.5rem 0 -0.5rem',
        padding: '0 0.5rem',
    },
}));

type PublicPropertyCardProps = {
    property: AvailablePropertyFragmentExtra;

}

const calculateOfficeSize = (
    maxSize: number | undefined | null,
    minSize: number | undefined | null,
    sizeUnit: SizeUnit | undefined = SizeUnit.SquareFeet,
    useRSF?: boolean,
): string | null => {
    const sizeUnitString = useRSF ? 'RSF' : sizeUnits[sizeUnit];
    if (minSize !== null && minSize !== undefined) {
        if (minSize >= 0 && maxSize) {
            if (minSize !== maxSize) {
                return `${formatNumber(
                    minSize,
                    { style: 'decimal', minimumFractionDigits: 0 }
                )} - ${formatNumber(
                    maxSize,
                    { style: 'decimal', minimumFractionDigits: 0 }
                )} ${sizeUnitString} `;
            }
            if (minSize === maxSize) {
                return `${formatNumber(
                    minSize,
                    { style: 'decimal', minimumFractionDigits: 0 }
                )} ${sizeUnitString} `;
            }
        } else if (maxSize && !minSize) {
            return `${formatNumber(
                minSize,
                { style: 'decimal', minimumFractionDigits: 0 }
            )} ${sizeUnitString} `;
        } else if (!maxSize && minSize) {
            return `${formatNumber(
                minSize,
                { style: 'decimal', minimumFractionDigits: 0 }
            )} ${sizeUnitString} `;
        }
    }
    return null;
};

const calculateOfficeDesks = (
    name: string,
    maxDesks: number | undefined | null,
    minDesks: number | undefined | null,
): string | null => {
    if (minDesks !== null && minDesks !== undefined) {
        if (minDesks >= 0 && maxDesks) {
            if (minDesks !== maxDesks) {
                return `${formatNumber(
                    minDesks,
                    { style: 'decimal', minimumFractionDigits: 0 }
                )} - ${formatNumber(
                    maxDesks,
                    { style: 'decimal', minimumFractionDigits: 0 }
                )} desks `;
            }
            if (minDesks === maxDesks) {
                return `${formatNumber(
                    minDesks,
                    { style: 'decimal', minimumFractionDigits: 0 }
                )} desks `;
            }
        } else if (maxDesks && !minDesks) {
            return `${formatNumber(
                maxDesks,
                { style: 'decimal', minimumFractionDigits: 0 }
            )} desks `;
        } else if (!maxDesks && minDesks) {
            return `${formatNumber(
                minDesks,
                { style: 'decimal', minimumFractionDigits: 0 }
            )} desks `;
        }
    }
    return null;
};

function PropertyCard({ property }: PublicPropertyCardProps) {
    const theme = useTheme();
    const { data: regionsData } = useRegionsQuery({
        fetchPolicy: 'cache-first',
        context: {
            headers: {
                brokeroverride: 'DIRECTACCESS',
            },
        },
    });
    const classes = useStyles();
    const useRSF = property.region.name.toLowerCase() === 'new york'
        || property.region.parentRegion?.name.toLowerCase() === 'new york';
    const officeCount = property.office.length;
    const fitted = property.office.filter((office) => (
        (office.fitted === Fitted.Fitted && (((office.desks || 0) > 0) || ((office.size || 0) > 0)))
    )).length > 0;
    let images = orderMediaPosition(property.image.map((image) => ({ ...image })));
    const leasedOfficesHavePrice = property.propertyType === PropertyType.Lease
        && property.office.some(({ price }) => price);
    const listPriceUnits = useRSF ? '/RSF' : `/${sizeUnits[property.office[0].sizeUnit]}`;
    const isLease = property.propertyType === PropertyType.Lease;
    const formattedMinCost = (isLease && property.minCost === 0) ? 'P.O.A' : formatNumber(
        property.minCost || 0,
        { maximumFractionDigits: 2, minimumFractionDigits: 2 },
        property.region.currency.id
    );
    const minCostUnits = leasedOfficesHavePrice ? listPriceUnits : '/m';
    const priceFrom = `${officeCount > 1 && formattedMinCost !== 'P.O.A'
        ? 'From ' : ''} ${formattedMinCost}${(isLease && property.minCost === 0)
        ? '' : minCostUnits}`;
    const size = calculateOfficeSize(
        property.maxAvailableSize,
        property.minAvailableSize,
        property.office[0].sizeUnit,
        useRSF
    );

    const desks = calculateOfficeDesks(
        property.name,
        property.maxDesks,
        property.minDesks,
    );

    if (property.featureImage) {
        const elementPos = images.map((image) => image.filename).indexOf(property.featureImage?.filename);
        images.splice(elementPos, 1);
        images.unshift({ ...property.featureImage });
    }

    if (!images.length) {
        images = [{
            id: 'comingSoon',
            location: 'comingSoon.jpg',
            type: 'image',
        }];
    }

    images = images.slice(0, 10);

    return (
        property && (
            <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                    <CardSlider officeCount={officeCount} images={images} type={property.propertyType} />
                    <Grid container direction="column" spacing={1} className={classes.cardContentContainer}>
                        <Grid item>
                            <Name name={property.name} location={property.region.name} />
                        </Grid>
                        {property.landmark.length > 0 && (
                            <Grid item>
                                <TubeStation
                                    name={property.landmark[0].name}
                                    distance={property.landmark[0].distance || 0}
                                    regionId={property.region.id}
                                    regions={regionsData?.regions || []}
                                    iconColor={theme.palette.primary.main}
                                />
                            </Grid>
                        )}
                        <Grid item>
                            {size && <PropertyInfoItem title="Size" value={size} />}
                            {desks && <PropertyInfoItem title="Desks" value={desks} />}
                            <PropertyInfoItem title="List Price" value={priceFrom} />
                            <PropertyInfoItem title="" value={fitted ? 'Fitted' : 'Non-Fitted'} />
                        </Grid>
                        <Grid item className={classes.cardActionsContainer}>
                            <Button className={classes.cardActionButton} variant="contained" color="primary">
                                See More Info
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        )
    );
}

export default PropertyCard;
